import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../utils/api';
import Modal from '../components/Modal';

interface Job {
  id: string;
  jobType: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'expired';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  payload: any;
  result?: any;
  errorMsg?: string;
  attempts: number;
  maxAttempts: number;
  processedBy?: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  expiresAt: string;
  apiKey?: {
    keyName: string;
    user: {
      username: string;
    };
  };
}

interface JobStats {
  total: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  expired: number;
}

const Jobs: React.FC = () => {
  const { hasPermission } = useAuth();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobStats, setJobStats] = useState<JobStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Filter state
  const [filterStatus, setFilterStatus] = useState('');
  const [filterJobType, setFilterJobType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Separate input state for debounce
  const [isSearching, setIsSearching] = useState(false);
  const [searchInfo, setSearchInfo] = useState<{ foundJobs: number; method?: string } | null>(null);

  // Bulk selection state
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);

  // Bulk cancel modal state
  const [showBulkCancelModal, setShowBulkCancelModal] = useState(false);
  const [showCancelByFilterModal, setShowCancelByFilterModal] = useState(false);
  const [bulkCancelLoading, setBulkCancelLoading] = useState(false);
  const [cancelFilterType, setCancelFilterType] = useState('');
  const [cancelFilterStatus, setCancelFilterStatus] = useState('');

  // Cleanup old jobs state
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(10);
  const [cleanupResult, setCleanupResult] = useState<{
    deleted: number;
    durationMs: number;
    cancelled?: boolean;
    totalToDelete?: number;
  } | null>(null);
  const [cleanupProgress, setCleanupProgress] = useState<{
    status: string;
    totalToDelete: number;
    deleted: number;
    startTime: number;
  } | null>(null);

  // Notification state
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  // Poll cleanup progress
  const pollCleanupProgress = useCallback(async () => {
    try {
      const response = await apiRequest('/api/whatsapp-management/jobs/cleanup-progress');
      const data = await response.json();
      if (data.success && data.data) {
        setCleanupProgress(data.data);
        return data.data;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  // Cleanup old jobs function
  const handleCleanupOldJobs = async () => {
    if (!hasPermission('whatsapp', 'delete')) {
      showNotification('error', 'Bạn không có quyền xóa jobs');
      return;
    }

    try {
      setCleanupLoading(true);
      setCleanupResult(null);
      setCleanupProgress(null);

      // Start polling progress
      const pollInterval = setInterval(async () => {
        const progress = await pollCleanupProgress();
        if (progress && (progress.status === 'completed' || progress.status === 'cancelled')) {
          clearInterval(pollInterval);
        }
      }, 500);

      const response = await apiRequest(`/api/whatsapp-management/jobs/cleanup-old?days=${cleanupDays}`, {
        method: 'DELETE'
      });

      clearInterval(pollInterval);
      const data = await response.json();

      if (response.ok) {
        setCleanupResult({
          deleted: data.deleted,
          durationMs: data.durationMs,
          cancelled: data.cancelled,
          totalToDelete: data.totalToDelete
        });
        setCleanupProgress(null);
        fetchJobs();
        fetchJobStats();
      } else {
        showNotification('error', data.error || 'Không thể xóa jobs cũ');
        setCleanupProgress(null);
      }
    } catch (err) {
      showNotification('error', 'Lỗi kết nối server');
      setCleanupProgress(null);
    } finally {
      setCleanupLoading(false);
    }
  };

  // Cancel cleanup
  const handleCancelCleanup = async () => {
    try {
      await apiRequest('/api/whatsapp-management/jobs/cleanup-cancel', {
        method: 'POST'
      });
      showNotification('success', 'Đã gửi yêu cầu dừng...');
    } catch {
      showNotification('error', 'Không thể gửi yêu cầu dừng');
    }
  };

  // Reset cleanup modal state
  const closeCleanupModal = () => {
    setShowCleanupModal(false);
    setCleanupResult(null);
    setCleanupProgress(null);
    setCleanupDays(10);
  };

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      setJobStats(null); // Reset stats khi bắt đầu load

      // Show searching indicator if search query exists
      if (searchQuery) {
        setIsSearching(true);
      }

      // Build query params
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString()
      });

      if (filterStatus) params.append('status', filterStatus);
      if (filterJobType) params.append('jobType', filterJobType);
      if (searchQuery) params.append('search', searchQuery);

      const response = await apiRequest(`/api/whatsapp-management/jobs?${params.toString()}`);

      if (response.ok) {
        const data = await response.json();
        setJobs(data.data || []);
        setTotalPages(data.pagination?.pages || 1);
        setTotalJobs(data.pagination?.total || 0);
        setError('');
        resetSelection(); // Reset selection when jobs change

        // Store search info if available
        if (data.searchInfo) {
          setSearchInfo(data.searchInfo);
        } else {
          setSearchInfo(null);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Không thể tải danh sách jobs');
      }
    } catch (error) {
      setError('Lỗi kết nối server');
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [currentPage, pageSize, filterStatus, filterJobType, searchQuery]);

  const fetchJobStats = useCallback(async () => {
    try {
      const response = await apiRequest('/api/whatsapp-management/stats');
      
      if (response.ok) {
        const data = await response.json();
        setJobStats(data.data);
      } else {
        console.error('Stats API failed:', response.status);
      }
    } catch (error) {
      console.error('Stats fetch error:', error);
      // Ignore stats error - let table still render
    }
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1); // Reset to page 1 when search changes
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchJobs();
    fetchJobStats();

    // Auto-refresh every 30 minutes (1800000ms)
    const interval = setInterval(() => {
      fetchJobs();
      fetchJobStats();
    }, 1800000); // 30 minutes

    return () => clearInterval(interval);
  }, [fetchJobs, fetchJobStats]);

  // Bulk selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedJobs(new Set());
    } else {
      const allJobIds = new Set(jobs.map(job => job.id));
      setSelectedJobs(allJobIds);
    }
    setSelectAll(!selectAll);
  };

  const handleSelectJob = (jobId: string, event?: React.MouseEvent<HTMLElement>, jobIndex?: number) => {
    const newSelected = new Set(selectedJobs);

    // Check if Shift key is pressed and we have a last selected index
    if (event?.shiftKey && lastSelectedIndex !== null && jobIndex !== undefined) {
      const startIndex = Math.min(lastSelectedIndex, jobIndex);
      const endIndex = Math.max(lastSelectedIndex, jobIndex);

      // Select all jobs in the range
      for (let i = startIndex; i <= endIndex; i++) {
        if (jobs[i]) {
          newSelected.add(jobs[i].id);
        }
      }

      setLastSelectedIndex(jobIndex);
    } else {
      // Normal single selection - toggle
      if (newSelected.has(jobId)) {
        newSelected.delete(jobId);
      } else {
        newSelected.add(jobId);
      }

      if (jobIndex !== undefined) {
        setLastSelectedIndex(jobIndex);
      }
    }

    setSelectedJobs(newSelected);
    setSelectAll(newSelected.size === jobs.length);
  };

  const resetSelection = () => {
    setSelectedJobs(new Set());
    setSelectAll(false);
    setLastSelectedIndex(null);
  };

  // Bulk cancel selected jobs
  const handleBulkCancel = async () => {
    if (selectedJobs.size === 0) {
      showNotification('error', 'Vui lòng chọn ít nhất một job');
      return;
    }

    if (selectedJobs.size > 100) {
      showNotification('error', 'Tối đa 100 jobs mỗi lần hủy');
      return;
    }

    setShowBulkCancelModal(true);
  };

  const executeBulkCancel = async () => {
    try {
      setBulkCancelLoading(true);
      const jobIds = Array.from(selectedJobs);

      const response = await apiRequest('/api/whatsapp-management/jobs/bulk-cancel', {
        method: 'POST',
        body: JSON.stringify({ jobIds })
      });

      if (response.ok) {
        const data = await response.json();
        await fetchJobs();
        await fetchJobStats();
        setShowBulkCancelModal(false);
        resetSelection();
        showNotification('success', data.message);
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.error || 'Có lỗi xảy ra');
      }
    } catch (error) {
      showNotification('error', 'Lỗi kết nối server');
    } finally {
      setBulkCancelLoading(false);
    }
  };

  // Cancel all jobs by filter
  const handleCancelByFilter = async () => {
    if (!cancelFilterType && !cancelFilterStatus) {
      showNotification('error', 'Vui lòng chọn ít nhất một bộ lọc');
      return;
    }

    try {
      setBulkCancelLoading(true);

      const response = await apiRequest('/api/whatsapp-management/jobs/cancel-by-filter', {
        method: 'POST',
        body: JSON.stringify({
          jobType: cancelFilterType || undefined,
          status: cancelFilterStatus || undefined
        })
      });

      if (response.ok) {
        const data = await response.json();
        await fetchJobs();
        await fetchJobStats();
        setShowCancelByFilterModal(false);
        setCancelFilterType('');
        setCancelFilterStatus('');
        showNotification('success', data.message);
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.error || 'Có lỗi xảy ra');
      }
    } catch (error) {
      showNotification('error', 'Lỗi kết nối server');
    } finally {
      setBulkCancelLoading(false);
    }
  };

  const handleShowDetails = (job: Job) => {
    setSelectedJob(job);
    setShowDetailsModal(true);
  };

  const handleRetryJob = async (jobId: string) => {
    if (!window.confirm('Are you sure you want to retry this job?')) {
      return;
    }

    try {
      const response = await apiRequest(`/api/whatsapp-management/jobs/${jobId}/retry`, {
        method: 'POST'
      });

      if (response.ok) {
        fetchJobs();
        fetchJobStats();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to retry job');
      }
    } catch (error) {
      setError('Failed to retry job');
      console.error('Retry job error:', error);
    }
  };

  const handleCancelJob = async (jobId: string) => {
    if (!window.confirm('Are you sure you want to cancel this job?')) {
      return;
    }

    try {
      const response = await apiRequest(`/api/whatsapp-management/jobs/${jobId}/cancel`, {
        method: 'POST'
      });

      if (response.ok) {
        fetchJobs();
        fetchJobStats();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to cancel job');
      }
    } catch (error) {
      setError('Failed to cancel job');
      console.error('Cancel job error:', error);
    }
  };

  const getStatusStyle = (status: string) => {
    const baseStyle = {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600' as const
    };

    switch (status) {
      case 'pending':
        return { ...baseStyle, backgroundColor: '#fef3c7', color: '#92400e' };
      case 'processing':
        return { ...baseStyle, backgroundColor: '#dbeafe', color: '#1e40af' };
      case 'completed':
        return { ...baseStyle, backgroundColor: '#d1fae5', color: '#065f46' };
      case 'failed':
        return { ...baseStyle, backgroundColor: '#fee2e2', color: '#991b1b' };
      case 'expired':
        return { ...baseStyle, backgroundColor: '#f3f4f6', color: '#374151' };
      default:
        return { ...baseStyle, backgroundColor: '#f3f4f6', color: '#374151' };
    }
  };

  const getPriorityStyle = (priority: string) => {
    const baseStyle = {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600' as const
    };

    switch (priority) {
      case 'urgent':
        return { ...baseStyle, backgroundColor: '#fecaca', color: '#7f1d1d' };
      case 'high':
        return { ...baseStyle, backgroundColor: '#fed7aa', color: '#7c2d12' };
      case 'medium':
        return { ...baseStyle, backgroundColor: '#bfdbfe', color: '#1e3a8a' };
      case 'low':
        return { ...baseStyle, backgroundColor: '#e5e7eb', color: '#1f2937' };
      default:
        return { ...baseStyle, backgroundColor: '#e5e7eb', color: '#1f2937' };
    }
  };

  const formatPayload = (payload: any) => {
    if (typeof payload === 'object') {
      return JSON.stringify(payload, null, 2);
    }
    return String(payload);
  };

  if (!hasPermission('whatsapp', 'read')) {
    return (
      <Layout>
        <div className="text-center py-8">
          <p className="text-red-600">You don't have permission to view jobs.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: '32px 16px' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <h1 style={{ 
              fontSize: '24px', 
              fontWeight: '700', 
              color: '#1e293b', 
              margin: '0 0 8px 0' 
            }}>
              Job Queue Management
            </h1>
            <p style={{
              fontSize: '14px',
              color: '#64748b',
              margin: 0
            }}>
              Monitor and manage WhatsApp jobs
            </p>
            <p style={{
              fontSize: '12px',
              color: '#94a3b8',
              margin: '4px 0 0 0',
              fontStyle: 'italic'
            }}>
              Auto-refresh: 30 phút
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {hasPermission('whatsapp', 'delete') && (
              <button
                onClick={() => setShowCleanupModal(true)}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#b91c1c'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
              >
                🗑️ Giải phóng job cũ
              </button>
            )}
            <button
              onClick={() => {
                fetchJobs();
                fetchJobStats();
              }}
              style={{
                padding: '10px 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Job Statistics */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #f1f5f9',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>📊 Job Statistics</h2>
          {loading && !jobStats ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  border: '4px solid #e5e7eb', 
                  borderTop: '4px solid #3b82f6', 
                  borderRadius: '50%', 
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px'
                }}></div>
                <div style={{ color: '#64748b' }}>Loading statistics...</div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
              <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>
                  {jobStats ? (jobStats as any)?.total || 0 : jobs.length}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Total Jobs</div>
              </div>
              <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#fef3c7', borderRadius: '8px' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#d97706' }}>
                  {jobStats ? (jobStats as any)?.pending || 0 : jobs.filter(j => j.status === 'pending').length}
                </div>
                <div style={{ fontSize: '12px', color: '#92400e' }}>⏳ Pending</div>
              </div>
              <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#dbeafe', borderRadius: '8px' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#2563eb' }}>
                  {jobStats ? (jobStats as any)?.processing || 0 : jobs.filter(j => j.status === 'processing').length}
                </div>
                <div style={{ fontSize: '12px', color: '#1d4ed8' }}>⚡ Processing</div>
              </div>
              <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#d1fae5', borderRadius: '8px' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#059669' }}>
                  {jobStats ? (jobStats as any)?.completed || 0 : jobs.filter(j => j.status === 'completed').length}
                </div>
                <div style={{ fontSize: '12px', color: '#047857' }}>✅ Completed</div>
              </div>
              <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#fee2e2', borderRadius: '8px' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#dc2626' }}>
                  {jobStats ? (jobStats as any)?.failed || 0 : jobs.filter(j => j.status === 'failed').length}
                </div>
                <div style={{ fontSize: '12px', color: '#b91c1c' }}>❌ Failed</div>
              </div>
              <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#f1f5f9', borderRadius: '8px' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#64748b' }}>
                  {jobStats ? (jobStats as any)?.expired || 0 : jobs.filter(j => j.status === 'expired').length}
                </div>
                <div style={{ fontSize: '12px', color: '#475569' }}>⏰ Expired</div>
              </div>
            </div>
          )}
        </div>


        {error && (
          <div style={{
            backgroundColor: '#fecaca',
            border: '1px solid #f87171',
            color: '#dc2626',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '24px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Search Info */}
        {searchInfo && searchQuery && (
          <div style={{
            backgroundColor: searchInfo.method === 'id_search' ? '#d1fae5' : '#e0f2fe',
            border: searchInfo.method === 'id_search' ? '1px solid #6ee7b7' : '1px solid #7dd3fc',
            color: searchInfo.method === 'id_search' ? '#047857' : '#0369a1',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '24px',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>{searchInfo.method === 'id_search' ? '⚡' : '🔍'}</span>
            <span>
              Tìm thấy <strong>{searchInfo.foundJobs.toLocaleString()}</strong> kết quả
              {searchInfo.method === 'id_search' && (
                <span style={{ marginLeft: '8px', fontSize: '11px', opacity: 0.8 }}>
                  (tìm theo Job ID - nhanh)
                </span>
              )}
              {searchInfo.method === 'json_search' && (
                <span style={{ marginLeft: '8px', fontSize: '11px', opacity: 0.8 }}>
                  (tìm trong payload - có thể chậm)
                </span>
              )}
            </span>
          </div>
        )}

        {/* Filters */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #f1f5f9',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? '1fr'
              : isTablet
              ? '1fr 1fr'
              : '2fr 1fr 1fr auto auto',
            gap: '16px',
            alignItems: 'end'
          }}>
            {/* Search */}
            <div>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#475569',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🔍 Tìm kiếm
                {isSearching && (
                  <span style={{
                    fontSize: '11px',
                    color: '#3b82f6',
                    fontWeight: '400'
                  }}>
                    Đang tìm...
                  </span>
                )}
              </div>
              <input
                type="text"
                placeholder="Job ID hoặc payload..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Status Filter */}
            <div>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#475569',
                marginBottom: '8px'
              }}>
                📊 Status
              </div>
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              >
                <option value="">Tất cả</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="expired">Expired</option>
              </select>
            </div>

            {/* Job Type Filter */}
            <div>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#475569',
                marginBottom: '8px'
              }}>
                🔧 Job Type
              </div>
              <select
                value={filterJobType}
                onChange={(e) => {
                  setFilterJobType(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              >
                <option value="">Tất cả</option>
                <option value="send_message">Send Message</option>
                <option value="reply_message">Reply Message</option>
                <option value="get_groups">Get Groups</option>
                <option value="get_contacts">Get Contacts</option>
                <option value="get_chat_history">Get Chat History</option>
                <option value="login">Login</option>
                <option value="delete_profile">Delete Profile</option>
                <option value="change_proxy">Change Proxy</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div>
              <button
                onClick={() => {
                  setSearchInput('');
                  setSearchQuery('');
                  setFilterStatus('');
                  setFilterJobType('');
                  setCurrentPage(1);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  boxSizing: 'border-box'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e2e8f0'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
              >
                🔄 Reset
              </button>
            </div>

            {/* Cancel by Filter Button */}
            {hasPermission('whatsapp', 'update') && (
              <div>
                <button
                  onClick={() => setShowCancelByFilterModal(true)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    boxSizing: 'border-box'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#b91c1c'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                >
                  ❌ Hủy theo bộ lọc
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedJobs.size > 0 && (
          <div style={{
            position: 'fixed',
            top: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            backgroundColor: 'white',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
            borderRadius: '12px',
            padding: '16px 24px',
            border: '2px solid #3b82f6',
            minWidth: '400px',
            maxWidth: '90%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
              <div>
                <span style={{
                  fontSize: '14px',
                  color: '#1e40af',
                  fontWeight: '500'
                }}>
                  Đã chọn {selectedJobs.size} jobs
                </span>
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  onClick={handleBulkCancel}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#b91c1c'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                >
                  ❌ Hủy đã chọn
                </button>

                <button
                  onClick={resetSelection}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'white',
                    color: '#64748b',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  Bỏ chọn
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Jobs Table */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #f1f5f9',
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#475569', width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      style={{
                        cursor: 'pointer',
                        transform: 'scale(1.1)'
                      }}
                    />
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', width: '200px' }}>Job ID</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', width: '150px' }}>Key Name</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', width: '140px' }}>Job Type</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', width: '100px' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', width: '100px' }}>Priority</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', width: '80px' }}>Attempts</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', width: '180px' }}>Created At</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', width: '250px' }}>Error</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', width: '150px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} style={{ padding: '48px', textAlign: 'center', color: '#64748b' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          border: '2px solid #e2e8f0',
                          borderTop: '2px solid #3b82f6',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        Đang tải jobs...
                      </div>
                    </td>
                  </tr>
                ) : jobs.length === 0 ? (
                  <tr>
                    <td colSpan={10} style={{ padding: '48px', textAlign: 'center', color: '#64748b' }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                      <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>Không có jobs</div>
                      <div style={{ fontSize: '14px' }}>Chưa có job nào được tạo</div>
                    </td>
                  </tr>
                ) : (
                  jobs.map((job, index) => (
                    <tr
                      key={job.id}
                      style={{
                        borderBottom: index === jobs.length - 1 ? 'none' : '1px solid #f1f5f9',
                        backgroundColor: selectedJobs.has(job.id) ? '#eff6ff' : 'white'
                      }}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).tagName !== 'BUTTON' &&
                            (e.target as HTMLElement).tagName !== 'INPUT') {
                          e.stopPropagation();
                          handleSelectJob(job.id, e, index);
                        }
                      }}
                    >
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedJobs.has(job.id)}
                          readOnly
                          style={{
                            cursor: 'pointer',
                            transform: 'scale(1.1)',
                            pointerEvents: 'none'
                          }}
                          title="Shift+click để chọn khoảng"
                        />
                      </td>
                      <td style={{ padding: '12px', fontSize: '12px', color: '#475569', fontFamily: 'monospace' }}>
                        {job.id}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>
                        {job.apiKey?.keyName || 'N/A'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                        {job.jobType}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600',
                          backgroundColor: job.status === 'completed' ? '#d1fae5' : 
                                          job.status === 'failed' ? '#fee2e2' :
                                          job.status === 'processing' ? '#dbeafe' :
                                          job.status === 'pending' ? '#fef3c7' : '#f1f5f9',
                          color: job.status === 'completed' ? '#047857' : 
                                 job.status === 'failed' ? '#b91c1c' :
                                 job.status === 'processing' ? '#1d4ed8' :
                                 job.status === 'pending' ? '#92400e' : '#475569'
                        }}>
                          {job.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600',
                          backgroundColor: job.priority === 'urgent' ? '#fee2e2' : 
                                          job.priority === 'high' ? '#fed7aa' :
                                          job.priority === 'medium' ? '#dbeafe' : '#f1f5f9',
                          color: job.priority === 'urgent' ? '#b91c1c' : 
                                 job.priority === 'high' ? '#c2410c' :
                                 job.priority === 'medium' ? '#1d4ed8' : '#475569'
                        }}>
                          {job.priority}
                        </span>
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#1e293b', textAlign: 'center' }}>
                        {job.attempts}/{job.maxAttempts}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#1e293b' }}>
                        {new Date(job.createdAt).toLocaleString()}
                      </td>
                      <td style={{ padding: '12px', fontSize: '12px', color: '#dc2626', maxWidth: '250px' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={job.errorMsg}>
                          {job.errorMsg || '-'}
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleShowDetails(job)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#dbeafe',
                              color: '#1d4ed8',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '500',
                              cursor: 'pointer'
                            }}
                          >
                            View
                          </button>
                          
                          {job.status === 'failed' && hasPermission('whatsapp', 'update') && (
                            <button
                              onClick={() => handleRetryJob(job.id)}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: '#d1fae5',
                                color: '#047857',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '500',
                                cursor: 'pointer'
                              }}
                            >
                              Retry
                            </button>
                          )}
                          
                          {(job.status === 'pending' || job.status === 'processing') && hasPermission('whatsapp', 'update') && (
                            <button
                              onClick={() => handleCancelJob(job.id)}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: '#fee2e2',
                                color: '#b91c1c',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '500',
                                cursor: 'pointer'
                              }}
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && jobs.length > 0 && (
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #f1f5f9',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              {/* Page info */}
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalJobs)} of {totalJobs} jobs
              </div>

              {/* Page size selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', color: '#64748b' }}>Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              {/* Page navigation */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: currentPage === 1 ? '#f1f5f9' : 'white',
                    color: currentPage === 1 ? '#94a3b8' : '#475569',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontWeight: '500'
                  }}
                >
                  ⏮️ First
                </button>

                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: currentPage === 1 ? '#f1f5f9' : 'white',
                    color: currentPage === 1 ? '#94a3b8' : '#475569',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontWeight: '500'
                  }}
                >
                  ◀️ Prev
                </button>

                <span style={{
                  padding: '6px 12px',
                  fontSize: '14px',
                  color: '#1e293b',
                  fontWeight: '500'
                }}>
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: currentPage === totalPages ? '#f1f5f9' : 'white',
                    color: currentPage === totalPages ? '#94a3b8' : '#475569',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Next ▶️
                </button>

                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: currentPage === totalPages ? '#f1f5f9' : 'white',
                    color: currentPage === totalPages ? '#94a3b8' : '#475569',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Last ⏭️
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Job Details Modal */}
        <Modal
          isOpen={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          title={`Job Details - ${selectedJob?.jobType}`}
          size="lg"
        >
          {selectedJob && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Basic Info */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px',
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>📋 Job ID</div>
                  <div style={{ fontSize: '13px', fontFamily: 'monospace', color: '#1e293b', wordBreak: 'break-all' }}>{selectedJob.id}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>🔧 Job Type</div>
                  <div style={{ fontSize: '13px', color: '#1e293b', fontWeight: '500' }}>{selectedJob.jobType}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>📊 Status</div>
                  <span style={getStatusStyle(selectedJob.status)}>
                    {selectedJob.status}
                  </span>
                </div>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>⚡ Priority</div>
                  <span style={getPriorityStyle(selectedJob.priority)}>
                    {selectedJob.priority}
                  </span>
                </div>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>🔄 Attempts</div>
                  <div style={{ fontSize: '13px', color: '#1e293b' }}>{selectedJob.attempts}/{selectedJob.maxAttempts}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>⚙️ Processed By</div>
                  <div style={{ fontSize: '13px', fontFamily: 'monospace', color: '#1e293b' }}>{selectedJob.processedBy || '-'}</div>
                </div>
              </div>

              {/* Timestamps */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px',
                padding: '16px',
                backgroundColor: '#fefce8',
                borderRadius: '8px',
                border: '1px solid #fde047'
              }}>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#854d0e', marginBottom: '4px' }}>🕐 Created At</div>
                  <div style={{ fontSize: '13px', color: '#422006' }}>{new Date(selectedJob.createdAt).toLocaleString()}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#854d0e', marginBottom: '4px' }}>⏰ Expires At</div>
                  <div style={{ fontSize: '13px', color: '#422006' }}>{new Date(selectedJob.expiresAt).toLocaleString()}</div>
                </div>
                {selectedJob.startedAt && (
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#854d0e', marginBottom: '4px' }}>▶️ Started At</div>
                    <div style={{ fontSize: '13px', color: '#422006' }}>{new Date(selectedJob.startedAt).toLocaleString()}</div>
                  </div>
                )}
                {selectedJob.completedAt && (
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#854d0e', marginBottom: '4px' }}>✅ Completed At</div>
                    <div style={{ fontSize: '13px', color: '#422006' }}>{new Date(selectedJob.completedAt).toLocaleString()}</div>
                  </div>
                )}
              </div>

              {/* Payload */}
              <div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1e293b',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  📦 Payload
                </div>
                <pre style={{
                  backgroundColor: '#f1f5f9',
                  padding: '16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  overflowX: 'auto',
                  border: '1px solid #cbd5e1',
                  fontFamily: 'monospace',
                  lineHeight: '1.6',
                  color: '#1e293b'
                }}>
                  {formatPayload(selectedJob.payload)}
                </pre>
              </div>

              {/* Result */}
              {selectedJob.result && (
                <div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#1e293b',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    ✅ Result
                  </div>
                  <pre style={{
                    backgroundColor: '#dcfce7',
                    padding: '16px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    overflowX: 'auto',
                    border: '1px solid #86efac',
                    fontFamily: 'monospace',
                    lineHeight: '1.6',
                    color: '#14532d'
                  }}>
                    {formatPayload(selectedJob.result)}
                  </pre>
                </div>
              )}

              {/* Error */}
              {selectedJob.errorMsg && (
                <div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#1e293b',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    ❌ Error Message
                  </div>
                  <div style={{
                    backgroundColor: '#fee2e2',
                    padding: '16px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#991b1b',
                    border: '1px solid #fca5a5',
                    lineHeight: '1.6'
                  }}>
                    {selectedJob.errorMsg}
                  </div>
                </div>
              )}

              {/* API Key Info */}
              {selectedJob.apiKey && (
                <div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#1e293b',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    🔑 API Key
                  </div>
                  <div style={{
                    backgroundColor: '#dbeafe',
                    padding: '16px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    border: '1px solid #93c5fd',
                    lineHeight: '1.8'
                  }}>
                    <div style={{ marginBottom: '8px' }}>
                      <strong style={{ color: '#1e40af' }}>Key Name:</strong>{' '}
                      <span style={{ color: '#1e3a8a' }}>{selectedJob.apiKey.keyName}</span>
                    </div>
                    <div>
                      <strong style={{ color: '#1e40af' }}>User:</strong>{' '}
                      <span style={{ color: '#1e3a8a' }}>{selectedJob.apiKey.user.username}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* Bulk Cancel Modal */}
        {showBulkCancelModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1e293b',
                margin: '0 0 20px 0',
                textAlign: 'center'
              }}>
                Bạn có chắc chắn muốn hủy {selectedJobs.size} jobs đã chọn?
              </h3>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center'
              }}>
                <button
                  onClick={() => setShowBulkCancelModal(false)}
                  disabled={bulkCancelLoading}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: 'white',
                    color: '#64748b',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: bulkCancelLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  Hủy bỏ
                </button>
                <button
                  onClick={executeBulkCancel}
                  disabled={bulkCancelLoading}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: bulkCancelLoading ? 'not-allowed' : 'pointer',
                    opacity: bulkCancelLoading ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {bulkCancelLoading && <div className="loading-spinner" style={{ width: '16px', height: '16px' }}></div>}
                  {bulkCancelLoading ? 'Đang hủy...' : `Hủy ${selectedJobs.size} jobs`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancel by Filter Modal */}
        {showCancelByFilterModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1e293b',
                margin: '0 0 20px 0'
              }}>
                Hủy jobs theo bộ lọc
              </h3>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Job Type:
                </label>
                <select
                  value={cancelFilterType}
                  onChange={(e) => setCancelFilterType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Tất cả</option>
                  <option value="send_message">Send Message</option>
                  <option value="reply_message">Reply Message</option>
                  <option value="get_groups">Get Groups</option>
                  <option value="get_contacts">Get Contacts</option>
                  <option value="get_chat_history">Get Chat History</option>
                  <option value="login">Login</option>
                  <option value="delete_profile">Delete Profile</option>
                  <option value="change_proxy">Change Proxy</option>
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Status:
                </label>
                <select
                  value={cancelFilterStatus}
                  onChange={(e) => setCancelFilterStatus(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Tất cả</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                </select>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => {
                    setShowCancelByFilterModal(false);
                    setCancelFilterType('');
                    setCancelFilterStatus('');
                  }}
                  disabled={bulkCancelLoading}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: 'white',
                    color: '#64748b',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: bulkCancelLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  Hủy bỏ
                </button>
                <button
                  onClick={handleCancelByFilter}
                  disabled={bulkCancelLoading || (!cancelFilterType && !cancelFilterStatus)}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: (bulkCancelLoading || (!cancelFilterType && !cancelFilterStatus)) ? 'not-allowed' : 'pointer',
                    opacity: (bulkCancelLoading || (!cancelFilterType && !cancelFilterStatus)) ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {bulkCancelLoading && <div className="loading-spinner" style={{ width: '16px', height: '16px' }}></div>}
                  {bulkCancelLoading ? 'Đang hủy...' : 'Hủy jobs'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Notification */}
        {notification && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 2000,
            backgroundColor: notification.type === 'success' ? '#d1fae5' : '#fee2e2',
            border: `1px solid ${notification.type === 'success' ? '#6ee7b7' : '#fca5a5'}`,
            color: notification.type === 'success' ? '#047857' : '#dc2626',
            padding: '12px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
            fontSize: '14px',
            fontWeight: '500',
            maxWidth: '400px',
            animation: 'slideIn 0.3s ease-out'
          }}>
            {notification.message}
          </div>
        )}

        {/* Cleanup Old Jobs Modal */}
        <Modal isOpen={showCleanupModal} onClose={cleanupLoading ? () => {} : closeCleanupModal} title="🗑️ Giải phóng Job Cũ">
          <div style={{ padding: '16px' }}>
            {cleanupResult ? (
              // Show result after cleanup
              <div>
                <div style={{
                  backgroundColor: cleanupResult.cancelled ? '#fef3c7' : '#d1fae5',
                  border: `1px solid ${cleanupResult.cancelled ? '#fcd34d' : '#6ee7b7'}`,
                  borderRadius: '12px',
                  padding: '24px',
                  textAlign: 'center',
                  marginBottom: '20px'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>
                    {cleanupResult.cancelled ? '⏹️' : '✅'}
                  </div>
                  <h3 style={{
                    color: cleanupResult.cancelled ? '#92400e' : '#047857',
                    fontSize: '20px',
                    fontWeight: '600',
                    margin: '0 0 8px 0'
                  }}>
                    {cleanupResult.cancelled ? 'Đã dừng!' : 'Xóa thành công!'}
                  </h3>
                  <p style={{
                    color: cleanupResult.cancelled ? '#78350f' : '#065f46',
                    fontSize: '32px',
                    fontWeight: '700',
                    margin: '0 0 8px 0'
                  }}>
                    {cleanupResult.deleted.toLocaleString()} jobs
                  </p>
                  {cleanupResult.cancelled && cleanupResult.totalToDelete && (
                    <p style={{ color: '#92400e', fontSize: '14px', margin: '0 0 8px 0' }}>
                      (Đã dừng, còn lại: {(cleanupResult.totalToDelete - cleanupResult.deleted).toLocaleString()} jobs)
                    </p>
                  )}
                  <p style={{ color: cleanupResult.cancelled ? '#92400e' : '#047857', fontSize: '14px', margin: 0 }}>
                    Thời gian: {(cleanupResult.durationMs / 1000).toFixed(2)} giây
                  </p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <button
                    onClick={closeCleanupModal}
                    style={{
                      padding: '12px 32px',
                      backgroundColor: '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Đóng
                  </button>
                </div>
              </div>
            ) : cleanupLoading && cleanupProgress ? (
              // Show progress while cleanup is running
              <div>
                <div style={{
                  backgroundColor: '#eff6ff',
                  border: '1px solid #93c5fd',
                  borderRadius: '12px',
                  padding: '24px',
                  textAlign: 'center',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    border: '4px solid #e5e7eb',
                    borderTop: '4px solid #3b82f6',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 16px'
                  }}></div>
                  <h3 style={{ color: '#1e40af', fontSize: '18px', fontWeight: '600', margin: '0 0 12px 0' }}>
                    Đang xóa...
                  </h3>
                  <p style={{ color: '#1e40af', fontSize: '28px', fontWeight: '700', margin: '0 0 8px 0' }}>
                    {cleanupProgress.deleted.toLocaleString()} / {cleanupProgress.totalToDelete.toLocaleString()}
                  </p>
                  {/* Progress bar */}
                  <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#dbeafe',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      width: `${Math.min(100, (cleanupProgress.deleted / cleanupProgress.totalToDelete) * 100)}%`,
                      height: '100%',
                      backgroundColor: '#3b82f6',
                      transition: 'width 0.3s ease'
                    }}></div>
                  </div>
                  <p style={{ color: '#3b82f6', fontSize: '14px', margin: 0 }}>
                    {((cleanupProgress.deleted / cleanupProgress.totalToDelete) * 100).toFixed(1)}% hoàn thành
                  </p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <button
                    onClick={handleCancelCleanup}
                    style={{
                      padding: '12px 32px',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    ⏹️ Dừng xóa
                  </button>
                </div>
              </div>
            ) : (
              // Show form before cleanup
              <>
                <p style={{ color: '#64748b', marginBottom: '16px', fontSize: '14px' }}>
                  Xóa vĩnh viễn các jobs được tạo trước đây hơn số ngày bạn chọn để giải phóng bộ nhớ và tránh tràn database.
                </p>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                    Xóa jobs cũ hơn (ngày):
                  </label>
                  <input
                    type="number"
                    value={cleanupDays}
                    onChange={(e) => setCleanupDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 10)))}
                    min={1}
                    max={365}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                  <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                    Mặc định: 10 ngày. Phạm vi: 1-365 ngày.
                  </p>
                </div>

                <div style={{
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fcd34d',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '20px'
                }}>
                  <p style={{ color: '#92400e', fontSize: '13px', margin: 0 }}>
                    ⚠️ <strong>Cảnh báo:</strong> Hành động này không thể hoàn tác. Tất cả jobs (bao gồm completed, failed, expired) cũ hơn {cleanupDays} ngày sẽ bị xóa vĩnh viễn.
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={closeCleanupModal}
                    disabled={cleanupLoading}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#f1f5f9',
                      color: '#475569',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: cleanupLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Hủy
                  </button>
                  <button
                    onClick={handleCleanupOldJobs}
                    disabled={cleanupLoading}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: cleanupLoading ? '#9ca3af' : '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: cleanupLoading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {cleanupLoading ? (
                      <>
                        <span style={{
                          width: '14px',
                          height: '14px',
                          border: '2px solid #fff',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></span>
                        Đang xóa...
                      </>
                    ) : (
                      <>🗑️ Xóa jobs cũ</>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

export default Jobs;